import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getPlanData, getStorageSize } from '../services/api/AuthApi';

const StorageContext = createContext();

export const StorageProvider = ({ children }) => {
    const { company } = useAuth();
    const [storageData, setStorageData] = useState({
        usedBytes: 0,
        totalBytes: 5 * 1024 * 1024 * 1024, // Default 5GB in bytes
        formattedUsed: "0 MB",
        formattedTotal: "5 GB",
        percentage: 0,
        isLoading: true
    });

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const refreshStorage = useCallback(async () => {
        if (!company || !company._id || !company.planId) return;

        try {
            // Fetch storage size and plan data concurrently
            const [storageRes, planRes] = await Promise.all([
                getStorageSize(company._id),
                getPlanData(company.planId)
            ]);

            const usedBytes = storageRes.data?.size?.bytes || 0;
            const formattedUsed = storageRes.data?.size?.formatted || formatBytes(usedBytes);

            // Determine total storage from plan, default to 5GB (5368709120 bytes) if not found
            let totalBytes = 5 * 1024 * 1024 * 1024;
            if (planRes.data && planRes.data.storage) {
                totalBytes = planRes.data.storage;
            }

            const percentage = Math.min((usedBytes / totalBytes) * 100, 100);

            setStorageData({
                usedBytes,
                totalBytes,
                formattedUsed,
                formattedTotal: formatBytes(totalBytes),
                percentage,
                isLoading: false
            });
        } catch (error) {
            console.error("Failed to fetch storage info:", error);
            setStorageData(prev => ({ ...prev, isLoading: false }));
        }
    }, [company]);

    // Automatically fetch storage when the company logs in
    useEffect(() => {
        if (company) {
            refreshStorage();
        } else {
            // Reset if logged out
            setStorageData(prev => ({ ...prev, usedBytes: 0, percentage: 0, isLoading: true }));
        }
    }, [company, refreshStorage]);

    return (
        <StorageContext.Provider value={{ ...storageData, refreshStorage }}>
            {children}
        </StorageContext.Provider>
    );
};

export const useStorage = () => useContext(StorageContext);
